function formatMoney(cents) {
  const { settings } = window.themeVariables || {};
  const format = settings?.[settings.currencyCodeEnabled ? 'moneyWithCurrencyFormat' : 'moneyFormat'] || '{{amount}}';
  const type = format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1] || 'amount';
  const value = (cents / 100).toFixed(type.includes('no_decimals') ? 0 : 2);
  const [int, dec] = value.split('.');
  const [thousands, decimal] = type.includes('comma_separator') ? ['.', ','] : [',', '.'];
  const amount = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');

  return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
}

class BuyMoreQuantitySelector extends HTMLElement {
  #abortController;
  #quantityInput;
  #quantitySelector;
  #hiddenQuantityInput;
  #bulkMin;
  #formId;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.#bulkMin = parseInt(this.dataset.bulkMin, 10) || 2;
    this.#formId = this.getAttribute('form');
    this.#quantitySelector = this.querySelector('quantity-selector');
    this.#quantityInput = this.querySelector('quantity-selector input[name="quantity"]');
    const { signal } = this.#abortController;
    const form = this.#formId ? document.getElementById(this.#formId) : null;

    this.addEventListener('change', (event) => {
      if (event.target.matches('input[type="radio"][name^="buy-more-mode"]')) {
        form && (form.dataset.buyMoreSelectedMode = event.target.value);
        this.#applyMode(event.target.value);
        this.#updateAtcLabel();
      }

      if (event.target.matches('input[name="quantity"]')) {
        this.#updateAtcLabel();
      }
    }, { signal });

    form?.addEventListener('variant:change', (event) => {
      this.dataset.variantPrice = event.detail?.variant?.price;
      this.#updateAtcLabel();
    }, { signal });

    const savedMode = form?.dataset.buyMoreSelectedMode || 'bulk';
    const savedRadio = this.querySelector(`input[type="radio"][value="${savedMode}"]`);

    if (savedRadio) {
      savedRadio.checked = true;
    }

    this.#applyMode(savedMode);
    this.#updateAtcLabel();
  }

  disconnectedCallback() {
    const checkedRadio = this.querySelector('input[type="radio"][name^="buy-more-mode"]:checked');
    const form = this.#formId ? document.getElementById(this.#formId) : null;

    if (checkedRadio && form) {
      form.dataset.buyMoreSelectedMode = checkedRadio.value;
    }

    this.#removeHiddenQuantityInput();
    this.#abortController?.abort();
  }

  #updateAtcLabel() {
    const bulk = this.querySelector('input[type="radio"][name^="buy-more-mode"]:checked')?.value === 'bulk';
    const quantity = bulk ? parseInt(this.#quantityInput?.value, 10) || this.#bulkMin : 1;
    const unitPrice = parseInt(this.dataset.variantPrice, 10) || 0;
    const discount = parseFloat(this.dataset.discountPercentage) || 0;
    const lineTotal = unitPrice * quantity;
    const discountedLineTotal = bulk ? lineTotal - Math.round(lineTotal * discount / 100) : lineTotal;
    const discountedUnitPrice = unitPrice - Math.round(unitPrice * discount / 100);
    const label = (this.dataset.atcTemplate || '')
      .replace('@quantity@', quantity)
      .replace('@price@', formatMoney(discountedLineTotal));

    document.querySelectorAll('[data-buy-more-atc-label]').forEach((element) => {
      element.textContent = label;

      if (element.hasAttribute('rotated-text')) {
        element.setAttribute('rotated-text', label);
      }

      if (element.hasAttribute('up-text')) {
        element.setAttribute('up-text', label);
      }
    });

    const lineTotalElement = document.querySelector('[data-buy-more-line-total]');
    const unitPriceElement = document.querySelector('[data-buy-more-unit-price]');

    if (lineTotalElement) {
      lineTotalElement.textContent = bulk
        ? (this.dataset.lineTotalTemplate || '')
            .replace('@price@', formatMoney(discountedLineTotal))
            .replace('@quantity@', quantity)
        : formatMoney(unitPrice);
    }

    if (unitPriceElement) {
      unitPriceElement.hidden = !bulk;

      if (bulk) {
        unitPriceElement.textContent = (this.dataset.unitPriceTemplate || '')
          .replace('@price@', formatMoney(discountedUnitPrice));
      }
    }
  }

  #applyMode(mode) {
    if (!this.#quantityInput) {
      return;
    }

    const isBulk = mode === 'bulk';
    this.classList.toggle('is-single', !isBulk);

    if (isBulk) {
      this.#removeHiddenQuantityInput();
      this.#setQuantitySelectorEnabled(true);
      this.#quantityInput.min = String(this.#bulkMin);

      const currentValue = parseInt(this.#quantityInput.value, 10);

      if (Number.isNaN(currentValue) || currentValue < this.#bulkMin) {
        this.#setQuantityValue(this.#bulkMin);
      }

      return;
    }

    this.#ensureHiddenQuantityInput();
    this.#setQuantitySelectorEnabled(false);
  }

  #setQuantityValue(value) {
    if (this.#quantitySelector) {
      this.#quantitySelector.quantity = String(value);
      return;
    }

    this.#quantityInput.value = String(value);
    this.#quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #ensureHiddenQuantityInput() {
    if (this.#hiddenQuantityInput) {
      return;
    }

    this.#hiddenQuantityInput = document.createElement('input');
    this.#hiddenQuantityInput.type = 'hidden';
    this.#hiddenQuantityInput.name = 'quantity';
    this.#hiddenQuantityInput.value = '1';

    if (this.#formId) {
      this.#hiddenQuantityInput.setAttribute('form', this.#formId);
    }

    this.appendChild(this.#hiddenQuantityInput);
  }

  #removeHiddenQuantityInput() {
    this.#hiddenQuantityInput?.remove();
    this.#hiddenQuantityInput = null;
  }

  #setQuantitySelectorEnabled(enabled) {
    this.#quantityInput.disabled = !enabled;

    this.#quantitySelector?.querySelectorAll('.quantity-selector__button').forEach((button) => {
      button.toggleAttribute('disabled', !enabled);
    });
  }
}

if (!window.customElements.get('buy-more-quantity-selector')) {
  window.customElements.define('buy-more-quantity-selector', BuyMoreQuantitySelector);
}
